import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {Observable, map} from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from './user.model';
import { APIResponse } from 'src/app/shared/data-access/api-response.model';
import {catchApiError} from "../../shared/utils/api-error";
import {encodeParam} from "../../shared/utils/query-string";

@Injectable({
  providedIn: 'root',
})
export class SystemManagementService {
  private httpClient = inject(HttpClient);

  createOrg(name: string, organization_id?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/create?name=${encodeParam(name)}`;
    if(organization_id != null) {
      apiUrl += `&organization_id=${encodeParam(organization_id)}`
    }

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  createSubject(name: string, organization_id: number, subject_id?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/subjects/create?name=${encodeParam(name)}&organization_id=${encodeParam(organization_id)}`;
    if(subject_id != null) {
      apiUrl += `&subject_id=${encodeParam(subject_id)}`
    }

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  createUnit(name: string, subject_id: number, unit_id?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/subjects/units/create?name=${encodeParam(name)}&subject_id=${encodeParam(subject_id)}`;
    if(unit_id != null) {
      apiUrl += `&unit_id=${encodeParam(unit_id)}`
    }

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  createCourse(name: string, unit_id: number, course_id?: number, start?: Date, end?: Date): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/subjects/units/courses/create?name=${encodeParam(name)}&unit_id=${encodeParam(unit_id)}`;
    if(course_id != null) {
      apiUrl += `&course_id=${encodeParam(course_id)}`
    }
    if(start != null) {
      apiUrl += `&start=${encodeParam(start)}`
    }
    if(end != null) {
      apiUrl += `&end=${encodeParam(end)}`
    }

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listOrgs(limit?: number, page?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/list`;
    if(limit != null && page != null)
      apiUrl += `?limit=${encodeParam(limit)}&page=${encodeParam(page)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listSubjects(limit?: number, page?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/subjects/list`;
    if(limit != null && page != null)
      apiUrl += `?limit=${encodeParam(limit)}&page=${encodeParam(page)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listUnits(limit?: number, page?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/subjects/units/list`;
    if(limit != null && page != null)
      apiUrl += `?limit=${encodeParam(limit)}&page=${encodeParam(page)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listCourses(limit?: number, page?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/subjects/units/courses/list`;
    if(limit != null && page != null)
      apiUrl += `?limit=${encodeParam(limit)}&page=${encodeParam(page)}`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  getGlobalBranding(): Observable<APIResponse>  {
    let apiUrl = `${environment.baseUrl}/organization/branding/get?global=true`;
    if(environment.__auth_email != null) {
      apiUrl = `/padbase/rest/public/organization/branding/get?global=true`;
    }

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  setGlobalBranding(json: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/branding/set?global=true`;
    const body = new HttpParams().set('json', json)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  addAnnouncement(msg: string, dashboard: string): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/messages/add?dashboard=${encodeParam(dashboard)}`
    const body = new HttpParams().set('msg', msg)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listAnnouncements(dashboard: string, organization_id?: number, subject_id?: number, unit_id?: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/messages/current?dashboard=${encodeParam(dashboard)}`
    // TODO add parameters
    // organization_id, subject_id, unit_id and dashboard (universal, admin, instructor, student)

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listAllAnnouncements(): Observable<APIResponse> {
    // TODO org id of 1 is temporary
    let apiUrl = `${environment.baseUrl}/organization/messages/list`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  editAnnouncement(msg: string, message_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/messages/edit?message_id=${encodeParam(message_id)}`
    const body = new HttpParams().set('msg', msg)

    return this.httpClient.post<APIResponse>(apiUrl, body).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  deleteAnnouncement(message_id: number): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/organization/messages/delete?message_id=${encodeParam(message_id)}`

    return this.httpClient.post<APIResponse>(apiUrl, {}).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  listLogFiles(): Observable<APIResponse> {
    let apiUrl = `${environment.baseUrl}/system/logs/list`

    return this.httpClient.get<APIResponse>(apiUrl).pipe(
      map((response) => {
        return response
      }),
      catchApiError()
    );
  }

  readLogFile(filename: string): Observable<string> {
    let apiUrl = `${environment.baseUrl}/system/logs/read?filename=${encodeParam(filename)}`

    return this.httpClient.get(apiUrl, {responseType: 'text'}).pipe(
      map((response) => {
        return response
      }),
    );
  }
}
